import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraInput, SpecraOverlayDialog, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { DocumentEditor } from '@onlyoffice/document-editor-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RfiOutwardExport } from '../../components/RfiEvidence/RfiOutwardExport';
import { useOnlyOfficeConnector } from '../../contexts/OnlyOfficeConnectorContext';
import { colorForUserId, PresenceAvatars } from '../Editor/components/PresenceAvatars';
import { SaveStatus } from '../Editor/components/SaveStatus';
import { useEditSession } from '../Editor/hooks/useEditSession';
import { useRfiDocument } from './useRfiDocument';
function instance(id) {
    return window.DocEditor?.instances?.[id];
}
function download(content, name) {
    const url = URL.createObjectURL(new Blob([
        content.slice().buffer
    ], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    }));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    setTimeout(()=>URL.revokeObjectURL(url), 1000);
}
export function RfiSectionEditor({ rfiResponseProjectId, sectionId, title, registerReviewExport }) {
    const doc = useRfiDocument(rfiResponseProjectId, sectionId);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [restoreId, setRestoreId] = useState('');
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState('');
    const [recoverUrl, setRecoverUrl] = useState('');
    const [ready, setReady] = useState(false);
    const [presence, setPresence] = useState([]);
    const { setConnector } = useOnlyOfficeConnector();
    const documentId = doc.snapshot?.document?.id;
    const { data: session, error: sessionError, refetch } = useEditSession(documentId);
    // Freeze the actual OO session, even when shared queries refresh after AI/source activity.
    const [pinned, setPinned] = useState();
    useEffect(()=>{
        if (session && !pinned) setPinned({
            epoch: doc.epoch,
            session
        });
    }, [
        session,
        pinned,
        doc.epoch
    ]);
    useEffect(()=>{
        if (pinned && pinned.epoch !== doc.epoch) {
            setReady(false);
            void refetch().then((result)=>{
                if (result.data) setPinned({
                    epoch: doc.epoch,
                    session: result.data
                });
            });
        }
    }, [
        doc.epoch,
        pinned,
        refetch
    ]);
    const pending = useRef();
    const handlers = useRef();
    const config = useMemo(()=>{
        if (!pinned) return null;
        const parsed = JSON.parse(pinned.session.editorConfigJson);
        parsed.events = {
            ...parsed.events,
            onAppReady: ()=>{},
            onDocumentReady: ()=>handlers.current?.onDocumentReady(),
            onDocumentStateChange: (event)=>handlers.current?.onDocumentStateChange(event),
            onRequestUsers: (event)=>handlers.current?.onRequestUsers(event),
            onError: (event)=>handlers.current?.onError(event),
            onDownloadAs: async (event)=>{
                const request = pending.current;
                if (!request || !event.data?.url) return;
                clearTimeout(request.timer);
                pending.current = undefined;
                setRecoverUrl(event.data.url);
                try {
                    const response = await fetch(event.data.url);
                    if (!response.ok) throw new Error('Could not retrieve the exported DOCX. Use the recovery link or retry.');
                    request.resolve(new Uint8Array(await response.arrayBuffer()));
                } catch (err) {
                    request.reject(err instanceof Error ? err : new Error('DOCX download failed.'));
                }
            }
        };
        return parsed;
    }, [
        pinned
    ]);
    const editorId = `rfi-editor-${sectionId}-${pinned?.epoch ?? 0}`;
    const exportingRef = useRef(false);
    useEffect(()=>()=>{
            if (pending.current) {
                clearTimeout(pending.current.timer);
                pending.current.reject(new Error('Editor closed before DOCX export completed.'));
            }
        }, []);
    const exportBytes = useCallback(()=>new Promise((resolve, reject)=>{
            if (pending.current) {
                reject(new Error('Another Office export is in progress. Wait and retry.'));
                return;
            }
            const editor = instance(editorId);
            if (!editor) {
                reject(new Error('The document is still opening. Retry when ready.'));
                return;
            }
            const timer = setTimeout(()=>{
                pending.current = undefined;
                reject(new Error('DOCX export timed out. Your editor is still open; retry export.'));
            }, 45000);
            pending.current = {
                resolve,
                reject,
                timer
            };
            editor.downloadAs('docx');
        }), [
        editorId
    ]);
    useEffect(()=>registerReviewExport?.(sectionId, exportBytes), [
        registerReviewExport,
        sectionId,
        exportBytes
    ]);
    const exportDraft = async (save)=>{
        if (exportingRef.current) return;
        exportingRef.current = true;
        setExporting(true);
        setExportError('');
        setRecoverUrl('');
        try {
            const bytes = await exportBytes();
            if (save) await doc.save(bytes);
            else download(bytes, `${title}-local-draft.docx`);
        } catch (err) {
            setExportError(err instanceof Error ? err.message : 'Could not export DOCX. Retry.');
        } finally{
            exportingRef.current = false;
            setExporting(false);
        }
    };
    const busy = doc.state === 'saving' || exporting;
    const unsaved = doc.state === 'dirty' || doc.state === 'conflict' || doc.state === 'error';
    useEffect(()=>{
        if (!unsaved) return;
        const protect = (event)=>{
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', protect);
        return ()=>window.removeEventListener('beforeunload', protect);
    }, [
        unsaved
    ]);
    handlers.current = {
        onDocumentReady: ()=>{
            setReady(true);
            const connector = instance(editorId)?.createConnector?.();
            if (connector) setConnector(connector, documentId);
        },
        onDocumentStateChange: (event)=>{
            if (event.data === true) doc.markDirty();
        },
        onRequestUsers: (event)=>{
            if (Array.isArray(event.data)) setPresence(event.data.filter((u)=>u.id !== config.editorConfig?.user?.id).map((u)=>({
                    ...u,
                    color: colorForUserId(u.id)
                })));
        },
        onError: (event)=>{
            void doc.reportError(event.data?.errorDescription || 'The document editor reported a save error. Download your local draft and retry.');
        }
    };
    if (!documentId) return /*#__PURE__*/ _jsxs("section", {
        "aria-live": "polite",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                children: doc.state === 'loading' ? 'Opening section document…' : doc.error
            }),
            doc.state === 'error' && /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>void doc.initialize(),
                children: "Retry opening section"
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        "data-testid": Testids.rfiEditor.surface,
        "data-document-id": documentId,
        "data-section-id": sectionId,
        "data-editor-ready": ready,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "flex flex-wrap items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        children: title
                    }),
                    /*#__PURE__*/ _jsx("output", {
                        "aria-live": "polite",
                        "data-testid": Testids.rfiEditor.saveState,
                        "data-state": exporting ? 'saving' : doc.state,
                        children: doc.state === 'conflict' || doc.state === 'error' ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "danger",
                            children: doc.state === 'conflict' ? 'Conflict — local draft retained' : 'Save failed — retry'
                        }) : /*#__PURE__*/ _jsx(SaveStatus, {
                            state: exporting || doc.state === 'saving' ? 'saving' : doc.state === 'dirty' ? 'dirty' : doc.state === 'saved' ? 'saved' : 'idle'
                        })
                    }),
                    /*#__PURE__*/ _jsx(PresenceAvatars, {
                        users: presence
                    }),
                    /*#__PURE__*/ _jsx(RfiOutwardExport, {
                        target: {
                            rfiResponseProjectId,
                            sectionId
                        }
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiEditor.save,
                        disabled: !ready || busy || doc.state === 'conflict',
                        onClick: ()=>void exportDraft(true),
                        children: doc.state === 'error' ? 'Retry save' : 'Save section'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.rfiEditor.exportDraft,
                        disabled: !ready || busy,
                        onClick: ()=>void exportDraft(false),
                        children: "Download internal recovery DOCX"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.rfiEditor.history,
                        onClick: ()=>{
                            setHistoryOpen((open)=>!open);
                            void doc.checkRemote();
                        },
                        children: "Version history"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: "Type or paste in the document. Comments, tracked revisions and selection belong to this same writing surface. Save section commits a new version. Recovery downloads contain local, unreviewed text. Use Export reviewed outward DOCX for external sharing."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        type: "file",
                        accept: ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                        "aria-label": "Import externally authored DOCX",
                        "data-testid": Testids.rfiEditor.importDocx,
                        disabled: busy || unsaved,
                        onChange: async (event)=>{
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            if (!file) return;
                            if (!file.name.toLowerCase().endsWith('.docx')) {
                                setExportError('Choose a DOCX document.');
                                return;
                            }
                            await doc.save(new Uint8Array(await file.arrayBuffer()), true);
                        }
                    }),
                    unsaved && /*#__PURE__*/ _jsx(SpecraText, {
                        size: "caption",
                        tone: "muted",
                        children: "Save your work before importing a replacement DOCX."
                    }),
                    (exportError || doc.error || sessionError) && /*#__PURE__*/ _jsxs("div", {
                        role: "alert",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                children: exportError || doc.error || 'The editor session could not be opened.'
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                onClick: ()=>{
                                    void doc.checkRemote();
                                    if (!pinned) void refetch();
                                },
                                children: "Retry connection"
                            })
                        ]
                    }),
                    recoverUrl && /*#__PURE__*/ _jsx("a", {
                        href: recoverUrl,
                        download: `${title}-local-draft.docx`,
                        children: "Recover exported local DOCX"
                    })
                ]
            }),
            doc.state === 'conflict' && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                className: "space-y-2 rounded-md border border-border bg-muted p-3",
                "data-testid": Testids.rfiEditor.conflict,
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        children: "The server has a different version. Keep editing here or download your local DOCX before comparing. No local content has been replaced."
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            "Local base: ",
                            doc.snapshot?.currentVersionId,
                            ". Server version:",
                            ' ',
                            doc.remote?.currentVersionId || 'Checking — retry connection to compare.'
                        ]
                    }),
                    doc.localContent && /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>doc.localContent && download(doc.localContent, `${title}-recovered-draft.docx`),
                        children: "Recover last attempted save"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.rfiEditor.downloadServer,
                        disabled: !doc.remote,
                        onClick: ()=>doc.remote && download(doc.remote.content, `${title}-server-${doc.remote.version?.versionNumber ?? ''}.docx`),
                        children: "Download server DOCX to compare"
                    })
                ]
            }),
            historyOpen && /*#__PURE__*/ _jsxs("aside", {
                className: "space-y-2 rounded-md border border-border p-3",
                "aria-label": "Section version history",
                children: [
                    doc.versions.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                        children: "Loading versions…"
                    }),
                    doc.versions.map((version)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap items-center gap-2",
                            "data-testid": Testids.rfiEditor.versionRow,
                            "data-version-id": version.id,
                            "data-version-number": version.versionNumber,
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    children: [
                                        "Version ",
                                        version.versionNumber,
                                        version.id === doc.snapshot?.currentVersionId ? ' · Current base' : '',
                                        version.restoredFromVersionId ? ` · Restored from ${version.restoredFromVersionId}` : ''
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    "data-testid": Testids.rfiEditor.restore,
                                    disabled: busy || unsaved || version.id === doc.snapshot?.currentVersionId,
                                    onClick: ()=>setRestoreId(version.id),
                                    children: "Restore as new version"
                                })
                            ]
                        }, version.id))
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: !!restoreId,
                onOpenChange: (open)=>{
                    if (!open && !busy) setRestoreId('');
                },
                title: "Restore this section version?",
                footer: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            disabled: busy,
                            onClick: ()=>setRestoreId(''),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.rfiEditor.confirmRestore,
                            disabled: busy,
                            onClick: async ()=>{
                                if (await doc.restore(restoreId)) setRestoreId('');
                            },
                            children: "Confirm restore"
                        })
                    ]
                }),
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: "This appends a new document version. Existing versions remain available. A stale restore will be rejected."
                })
            }),
            !pinned && /*#__PURE__*/ _jsx(SpecraText, {
                children: "Opening shared rich editor…"
            }),
            pinned && config && /*#__PURE__*/ _jsx("div", {
                className: "h-screen min-h-0",
                children: /*#__PURE__*/ _jsx(DocumentEditor, {
                    id: editorId,
                    documentServerUrl: pinned.session.documentServerUrl,
                    config: config
                })
            }, editorId)
        ]
    });
}
